import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/bootstrap.min.css';
import '../styles/style.css';
import MainSidebar from '../components/MainSidebar';
import only_free from '../assets/images/only_free.svg';
import verified from '../assets/images/verified.svg';
import all_list from '../assets/images/all_list.svg';
import refresh from '../assets/images/refresh.svg';
import whitedrop_down from '../assets/images/whitedrop_down.svg';
import default_user from '../assets/images/default_user.webp';
import default_header_image from '../assets/images/default_header_image.png';
import premiumicon from '../assets/images/sale-03.svg';
import CreatePostPage from '../components/CreatePostPage';
import HomeSidebarFooterLinks from '../components/HomeSidebarFooterLinks';
import SidebarSearch from '../components/SidebarSearch';
import config from '../config/config'; 

const CreatePost = () => {
  const [isRestricted, setIsRestricted] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [showOnlyFree, setShowOnlyFree] = useState(false);
  const [freeIcon, setFreeIcon] = useState(only_free); 
  const [restrictedUsers, setRestrictedUsers] = useState(new Set());
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false); 
  const [reportReason, setReportReason] = useState('');
  const [selectedReportReason, setSelectedReportReason] = useState('');
  const [showSaveToListModal, setShowSaveToListModal] = useState({ visible: false, userId: null });
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [listName, setListName] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [userLists, setUserLists] = useState([]); 
  const [apiResponseMessage, setApiResponseMessage] = useState('');
  const navigate = useNavigate();
  const [checkedLists, setCheckedLists] = useState(new Set());
  const { username } = useParams();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/Login');
    }
  }, [navigate]);

const fetchProfiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${config.baseURL}/get-creators${showOnlyFree ? '?showonlyfree=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setProfiles(data.data);

        // Initialize blockedUsers and restrictedUsers states
        const blocked = new Set();
        const restricted = new Set();
        data.data.forEach(profile => {
          if (profile.user_details.is_blocked) blocked.add(profile.user.id);
          if (profile.user_details.is_restricted) restricted.add(profile.user.id);
        });
        setBlockedUsers(blocked);
        setRestrictedUsers(restricted);

        setErrorMessage('');
      } else {
        setErrorMessage('Failed to fetch profiles');
      }
    } catch (error) {
      setErrorMessage('Error fetching profiles');
    }
  }, [showOnlyFree]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);


const fetchUserLists = async () => {
  try {
    const token = localStorage.getItem('token');
    const url = `${config.baseURL}/get-users-list`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    const data = await response.json();
    if (data.success) {
      setUserLists(data.data);
      // Initialize checkedLists state based on user list data
      const initialCheckedLists = new Set(data.data
        .filter(list => list.users.some(user => user.id === showSaveToListModal.userId))
        .map(list => list.id)
      );
      setCheckedLists(initialCheckedLists);
    } else {
      setUserLists([]);
    }
  } catch (error) {
    setUserLists([]);
  }
};

  useEffect(() => {
    if (showSaveToListModal) {
      fetchUserLists();
    }
  }, [showSaveToListModal]);


const handleCheckboxChange = async (listId, userId, isChecked) => {
  const url = `${config.baseURL}/${isChecked ? 'add-user-to-list' : 'remove-user-from-list'}`;
  const params = new URLSearchParams();
  params.append('user_list_id', listId);
  params.append('listed_user_id', userId);

  try {
    const token = localStorage.getItem('token'); // Assuming token is stored in local storage
    await axios.post(url, params, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    setCheckedLists(prevCheckedLists => {
      const updatedCheckedLists = new Set(prevCheckedLists);
      if (isChecked) {
        updatedCheckedLists.add(listId);
      } else {
        updatedCheckedLists.delete(listId);
      }
      return updatedCheckedLists;
    });
  } catch (error) {
    console.error('Error updating user list:', error);
  }
};



  const handleShowOnlyFreeClick = () => {
    setShowOnlyFree(prevState => !prevState);
    setFreeIcon(prevState => prevState === only_free ? all_list : only_free);
  };

  const handleRefreshClick = () => {
    fetchProfiles();
  };

  const handleCopyLink = (userName) => {
    const profileUrl = `${window.location.origin}/${userName}`;
    navigator.clipboard.writeText(profileUrl)
      .then(() => {
        setToastMessage('Profile link copied to clipboard!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      })
      .catch(() => {
        setErrorMessage('Error copying link');
      });
  };

 const handleRestrictUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const isRestricted = restrictedUsers.has(userId);
      const url = isRestricted ? `${config.baseURL}/unrestrict-user` : `${config.baseURL}/restrict-user`;
      const formData = isRestricted ? { unrestrict_user_id: userId } : { restrict_to: userId };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(formData)
      });

      const data = await response.json();
      if (data.success) {
        setRestrictedUsers(prevState => {
          const newState = new Set(prevState);
          if (isRestricted) {
            newState.delete(userId);
          } else {
            newState.add(userId);
          }
          return newState;
        });
        setToastMessage(isRestricted ? 'User unrestricted successfully!' : 'User restricted successfully!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setErrorMessage('');
      } else {
        setErrorMessage(isRestricted ? 'Failed to unrestrict user' : 'Failed to restrict user');
      }
    } catch (error) {
      setErrorMessage(isRestricted ? 'Error unrestricting user' : 'Error restricting user');
    }
  };

  const handleBlockUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseURL}/block-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ block_to: userId })
      });
      const data = await response.json();
      if (data.success) {
        setBlockedUsers(prevState => new Set(prevState).add(userId));
        setToastMessage('User blocked successfully!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setErrorMessage('');
      } else {
        setErrorMessage('Failed to block user');
      }
    } catch (error) {
      setErrorMessage('Error blocking user');
    }
  };

const handleUnblockUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseURL}/unblock-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ unblock_user_id: userId })
      });
      const data = await response.json();
      if (data.success) {
        setBlockedUsers(prevState => {
          const newState = new Set(prevState);
          newState.delete(userId);
          return newState;
        });
        setToastMessage('User unblocked successfully!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setErrorMessage('');
      } else {
        setErrorMessage('Failed to unblock user');
      }
    } catch (error) {
      setErrorMessage('Error unblocking user');
    }
  };

  const handleReportUser = (userId) => {
    setSelectedReportReason(''); // Reset the selected reason
    setReportReason(userId); // Set the userId for the report
    setShowModal(true);

  };

const handleShowSaveToListModal = (userId) => {
  console.log('Show Save to List Modal for userId:', userId);
  setShowSaveToListModal({ visible: true, userId });
};

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseURL}/report-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ report_to: reportReason, reason: selectedReportReason })
      });
      const data = await response.json();
      if (data.success) {
        setToastMessage('User reported successfully!');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        setErrorMessage('');
      } else {
        setErrorMessage('Failed to report user');
      }
    } catch (error) {
      setErrorMessage('Error reporting user');
    } finally {
      setShowModal(false);
    }
  };

  const handleSaveToListClick = () => {
    setShowSaveToListModal(true);
  };

  const handleCreateListClick = () => {
    setShowSaveToListModal(false);
    setShowCreateListModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const reportReasons = [
    'Violates OnlyFans Terms of Service',
    'Contains copyrighted material (DMCA)',
    'Child Sexual Abuse Material (CSAM)',
    'Report Spam',
    'Report Abuse'
  ];


  useEffect(() => {
    // Initialize the checked state based on userLists if needed
  }, [userLists]);

  const handleCreateListSubmit = async (e) => {
    e.preventDefault();
    try {
      if (listName.trim() === '') {
        setErrorMessage('List name cannot be empty');
        return;
      }
      if (listName.length > 64) {
        setErrorMessage('List name cannot exceed 64 characters');
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseURL}/create-user-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({ title: listName }) // Use 'title' instead of 'list_name'
      });

      const data = await response.json();
      if (data.success) {
        setApiResponseMessage('List created successfully!');
        setShowCreateListModal(false);
        setListName(''); // Clear list name
        setWordCount(0); // Reset word count
      } else {
        setApiResponseMessage('Failed to create list');
      }
    } catch (error) {
      setApiResponseMessage('Error creating list');
    }
  };

  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  };


  return (
    <div>
      <div className="main_patreonpass_wrapper"> 
          <div className="patreonpass_container">
            <div className="left_sidebar">
              <MainSidebar />
            </div>
            <div className="patreonpass_main_content" id="content">
              <div className="left_main_content">
                <div className="feed_wrapper create_post_none">                  
                  <CreatePostPage />
                </div>
              </div>
              <div className="right_main_content">
                <SidebarSearch />
                <div className="suggestion_box">
                  <div className="title_header">
                    <h2> SUGGESTIONS</h2>
                    <div className="only_free right_side_icons">
                      <Link className="free_only" onClick={handleShowOnlyFreeClick}>
                        <img src={freeIcon} alt="" />
                      </Link>
                    </div>
                    <div className="only_free right_side_icons">
                      <Link className="refresh_list" onClick={handleRefreshClick}>
                        <img src={refresh} alt="" />
                      </Link>
                    </div>                    
                  </div>
                  <div className="free_user_list">
                    <div className="swiper-container">
                      <div className="swiper-wrapper">                          
                        {profiles.map((profile) => (
                        <div key={profile.user.id} className="swiper-slide">
                          <div className="profile_info_card">
                            <div className="profile_info_card_link">
                              <div className="profile_item_cover">
                                <Link to={`/${profile.user.user_name}`}>
                                <img
                                  src={profile.user_details.user_back_profile || default_header_image}
                                  alt="Profile background"
                                />
                                </Link>
                              </div>
                              <div className="free_list">
                                {profile.user.subs_type === 'free' ? 'Free' : <img src={premiumicon} alt="Premium" />}
                              </div>
                              <div className="more_options">
                                <div className="head_dropdown">
                                  <ul>
                                    <li className="nav-item dropdown">
                                      <a
                                        className="nav-link dropdown-toggle"
                                        href="#"
                                        id={`productsDropdown-${profile.user.id}`}
                                        role="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                      >
                                        <img
                                          className="icon_dropdown"
                                          src={whitedrop_down}
                                          alt="Dropdown"
                                        />
                                      </a>
                                      <ul
                                        className="dropdown-menu"
                                        aria-labelledby={`productsDropdown-${profile.user.id}`}
                                      >
                                        <li className="dropdown">
                                          <a className="dropdown-item" href={`/${profile.user.user_name}`}>
                                            <span className={`user_dropdown ${profile.user.status === 'active' ? 'user_online_status' : ''}`}>
                                              <img
                                                src={profile.user_details.user_profile || default_user}
                                                alt="User"
                                              />
                                            </span>{" "}
                                            @{profile.user.user_name}
                                          </a>
                                        </li>
                                        <hr className="dropdown-divider" />
                                        <li className="dropdown">
                                          <a className="dropdown-item" onClick={() => handleCopyLink(profile.user.user_name)}>
                                            Copy link to profile
                                          </a>
                                        </li>
                                        <li className="dropdown">
                                          <a className="dropdown-item" onClick={() => handleShowSaveToListModal(profile.user.id)}>
                                            Add to / remove from lists
                                          </a>
                                        </li>
                                        <hr className="dropdown-divider" />
                                        <li className="dropdown">
                                          <a
                                            className="dropdown-item danger_link"
                                            href="#"
                                            onClick={() => handleRestrictUser(profile.user.id)}
                                          >
                                            {restrictedUsers.has(profile.user.id) ? 'Unrestrict' : 'Restrict'}
                                          </a>
                                        </li>
                                        <li className="dropdown">
                                          <a
                                            className={`dropdown-item ${blockedUsers.has(profile.user.id) ? 'danger_link' : 'success_link'}`}
                                            onClick={() => blockedUsers.has(profile.user.id) ? handleUnblockUser(profile.user.id) : handleBlockUser(profile.user.id)}
                                          >
                                            {blockedUsers.has(profile.user.id) ? 'Unblock' : 'Block'}
                                          </a>
                                        </li>
                                        <li className="dropdown">
                                          <a
                                            className="dropdown-item danger_link"
                                            onClick={() => handleReportUser(profile.user.id)}
                                          >
                                            Report
                                          </a>
                                        </li>
                                      </ul>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                              <div className="profile_content">
                                <div className={`profile_avatar ${profile.user.online_status === 'online' ? 'user_online_status' : ''}`}>
                                  <Link to={`/${profile.user.user_name}`} className="profile_link">
                                    {profile.user_details.user_profile ? (
                                      <img
                                        src={profile.user_details.user_profile}
                                        alt="Profile"
                                      />
                                    ) : (
                                      <span className="profile_name_none_image">
                                      {getInitials(profile.user.name)}
                                      </span>
                                    )}
                                  </Link>
                                </div>
                                <div className="profile_name"> 
                                  <Link to={`/${profile.user.user_name}`}>{profile.user.name} </Link>
                                  <svg className="home_sidebar_user_verify" width={16} height={16} viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M61.6076 13.7848L56.9968 6.10012C56.9652 6.04751 56.8964 6.03362 56.849 6.07253C52.6506 9.51474 42.1011 14.213 32.6436 6.08581C32.594 6.04321 32.5175 6.05367 32.4878 6.11189C30.1809 10.6355 22.8439 19.7553 11.3791 21.0382C11.3194 21.0449 11.2772 21.0993 11.2905 21.1579C12.475 26.3811 12.658 38.0206 4.1023 43.8173C4.05005 43.8527 4.03933 43.9254 4.08371 43.9702C8.22706 48.1592 15.3163 58.6835 11.3288 68.1314C11.3002 68.1992 11.3472 68.2756 11.4207 68.274C16.3178 68.1652 26.8065 70.985 30.9447 83.2021C30.9659 83.2648 31.039 83.2941 31.0941 83.2573C35.9852 79.9892 47.7732 75.534 56.8267 83.2349C56.8796 83.2799 56.9615 83.2638 56.9867 83.1991C58.9473 78.1609 65.5991 68.3556 77.0421 68.2789C77.1088 68.2785 77.1593 68.2175 77.1409 68.1533C75.4455 62.2367 74.8734 49.3511 85.8645 43.952C85.9282 43.9207 85.9427 43.834 85.8903 43.7863L80.2911 38.6962"
                                      stroke="#fefefe"
                                      strokeWidth={6}
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d="M30.9874 39.2152L44.481 51.6709L81.3291 15.3418"
                                      stroke="#fefefe"
                                      strokeWidth={6}
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </div>
                                <div className="profile_user_name">@{profile.user.user_name}</div>
                              </div>
                            </div>

                          </div>
                        </div>
                      ))} 

                      </div>
                      <div className="swiper-pagination" />
                    </div>
                  </div>
                </div>
                <HomeSidebarFooterLinks />
              </div>
            </div>
          </div>
        </div>

        <div className="share_text">
             {errorMessage && <div className="error_message">{errorMessage}</div>}
             {showToast && <div className="toast_message">{toastMessage}</div>}
        </div>

      {/* Save to List Modal */}
      {showSaveToListModal.visible && (
          <>
            <div className="modal-overlay-dark" onClick={() => setShowSaveToListModal({ visible: false, userId: null })}></div>
            <div className="modal restoreAccessModalAccess save_list_popup fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="saveToListModalLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="saveToListModalLabel">SAVE TO LIST</h5>
                  </div>
                  <div className="modal-body">
                    {userLists.length === 0 ? (
                      <div className="no_list_message">Nothing found</div>
                    ) : (
                      <ul>
                        {userLists.map(list => (
                          <li key={list.id}>
                            <div className="list_wrap_popup">
                              <div className="m-checkbox-row checkbox_table">
                                <input
                                  type="checkbox"
                                  checked={checkedLists.has(list.id)}
                                  onChange={(e) => {
                                    const userId = showSaveToListModal.userId;
                                    console.log(`Checkbox changed for listId: ${list.id}, userId: ${userId}`);
                                    handleCheckboxChange(list.id, userId, e.target.checked);
                                  }}
                                  name=""
                                  id={`checkbox-${list.id}`}
                                />
                                <label htmlFor={`checkbox-${list.id}`} className="g-input__wrapper"></label>
                              </div>
                              <div className="list_popup">
                                <div className="list_popup_left">
                                  <span>{list.title}</span>
                                  <small>{list.user_count} user</small>
                                </div>
                                <div className="list_popup_right">
                                  {list.users && list.users.length > 0 ? (
                                    list.users.map(user => (
                                      user.profile_image && (
                                        <img
                                          key={user.id}
                                          src={user.profile_image}
                                          alt={user.title}
                                          style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                                        />
                                      )
                                    ))
                                  ) : (
                                    list.profile_image && (
                                      <img
                                        src={list.profile_image}
                                        alt={list.title}
                                        style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                                      />
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-primary cancel_btn" onClick={handleCreateListClick}>
                      <svg
                        enableBackground="new 0 0 50 50"
                        height="20px"
                        id="Layer_1"
                        version="1.1"
                        viewBox="0 0 50 50"
                        width="20px"
                        xmlSpace="preserve"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                      >
                        <rect fill="none" height={20} width={20} />
                        <line
                          fill="none"
                          stroke="#00aff0"
                          strokeMiterlimit={10}
                          strokeWidth={4}
                          x1={9}
                          x2={41}
                          y1={25}
                          y2={25}
                        />
                        <line
                          fill="none"
                          stroke="#00aff0"
                          strokeMiterlimit={10}
                          strokeWidth={4}
                          x1={25}
                          x2={25}
                          y1={9}
                          y2={41}
                        />
                      </svg>
                      New list
                    </button>
                    <button type="button" className="btn btn-secondary cancel_btn" onClick={() => setShowSaveToListModal({ visible: false, userId: null })}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}





      {/* Create New List Modal */}
      {showCreateListModal && (
        <>
          <div className="modal-overlay-dark" onClick={() => setShowCreateListModal(false)}></div>
          <div id="restoreAccessForm" className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="createListModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="createListModalLabel">CREATE NEW LIST</h5>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleCreateListSubmit}>
                    <div className="position_relative">
                      <input
                        type="text"
                        className="form-control"
                        value={listName}
                        onChange={(e) => {
                          setListName(e.target.value);
                          setWordCount(e.target.value.length);
                        }}
                        maxLength="64"
                        placeholder=""
                      />
                      <label htmlFor="listname" className="placeholder-label">List Name</label>
                      <div className="word_count">{wordCount} / 64</div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-primary send_btn" onClick={() => setShowCreateListModal(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary send_btn">Save</button>
                    </div>
                  </form>
                  {apiResponseMessage && <p>{apiResponseMessage}</p>}
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Toast for user feedback */}
      {showToast && (
        <div className="toast">
          <p>{toastMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="error">
          <p>{errorMessage}</p>
        </div>
      )}

       {showModal && (
          <>
            <div className="modal-overlay-dark" onClick={handleCloseModal}></div>
            <div className="modal restoreAccessModalAccess fade show" style={{ display: 'block' }} tabIndex="-1" aria-labelledby="restoreAccessModalLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="restoreAccessModalLabel">Report</h5>
                  </div>
                  <div className="modal-body">
                    <form id="reportUserForm" onSubmit={handleModalSubmit}>
                      <div className="form-group">
                        {reportReasons.map((reason, index) => (
                          <div className="form-check" key={index}>
                            <input 
                              className="form-check-input" 
                              type="radio" 
                              name="reportReason" 
                              id={`reportReason${index}`} 
                              value={reason} 
                              onChange={(e) => setSelectedReportReason(e.target.value)} 
                              required 
                            />
                            <label className="form-check-label" htmlFor={`reportReason${index}`}>
                              {reason}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary cancel_btn" onClick={handleCloseModal}>Cancel</button>
                        <button type="submit" className="btn btn-primary send_btn" disabled={!selectedReportReason}>Submit</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

    </div>
  );
};

export default CreatePost;


